import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  Drawer, Button, Tag, Alert, Modal, message, Space, Spin,
} from 'antd';
import {
  SaveOutlined, DownloadOutlined, CopyOutlined, LinkOutlined,
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import getToken from '../../../../../logic/utils/getToken';

const FORMAT_COLORS = { json: 'blue', xml: 'orange', yaml: 'green' };
const LANGUAGE_BY_FORMAT = { json: 'json', xml: 'xml', yaml: 'yaml' };

function ConfigEditor({ file, onClose, onSaved }) {
  const open = !!file;
  const [content, setContent] = useState('');
  const [original, setOriginal] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const dirty = content !== original;
  const editorRef = useRef(null);

  useEffect(() => {
    if (!file) return;
    setError(null);
    setLoading(true);
    const token = getToken();
    axios.get(`/api/configs/${file.filename}`, { headers: { token } })
      .then((res) => {
        if (res.data.status === 'success') {
          setContent(res.data.data.content);
          setOriginal(res.data.data.content);
        } else {
          setError('Не удалось загрузить файл');
        }
      })
      .catch(() => setError('Не удалось загрузить файл'))
      .finally(() => setLoading(false));
  }, [file]);

  const handleSave = useCallback(async () => {
    if (!file || saving) return;
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      const res = await axios.put(`/api/configs/${file.filename}`, { content }, { headers: { token } });
      if (res.data.status === 'success') {
        message.success('Сохранено');
        setOriginal(content);
        onSaved();
      }
    } catch (e) {
      const txt = (e.response && e.response.data && e.response.data.error
        && e.response.data.error.text && e.response.data.error.text.rus)
        || 'Ошибка сохранения';
      setError(txt);
    } finally {
      setSaving(false);
    }
  }, [file, content, saving, onSaved]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && open) {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, handleSave]);

  useEffect(() => {
    const beforeUnload = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  const requestClose = () => {
    if (!dirty) { onClose(); return; }
    Modal.confirm({
      title: 'Закрыть без сохранения?',
      content: 'Несохранённые изменения будут потеряны.',
      okText: 'Закрыть',
      cancelText: 'Отмена',
      onOk: onClose,
    });
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyContent = async () => {
    try {
      await navigator.clipboard.writeText(content);
      message.success('Содержимое скопировано');
    } catch (e) { message.error('Не удалось скопировать'); }
  };

  const handleCopyUrl = async () => {
    const url = `${window.location.origin}/configs/${file.filename}`;
    try {
      await navigator.clipboard.writeText(url);
      message.success('URL скопирован');
    } catch (e) { message.error('Не удалось скопировать'); }
  };

  return (
    <Drawer
      title={file ? (
        <Space>
          <span>{file.filename}</span>
          <Tag color={FORMAT_COLORS[file.format]}>{file.format.toUpperCase()}</Tag>
          {dirty && <Tag color="orange">● несохранено</Tag>}
        </Space>
      ) : null}
      width="70%"
      open={open}
      onClose={requestClose}
      destroyOnClose
      footer={(
        <Space>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} disabled={!dirty}>
            Сохранить
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownload}>Скачать</Button>
          <Button icon={<CopyOutlined />} onClick={handleCopyContent}>Копировать содержимое</Button>
          <Button icon={<LinkOutlined />} onClick={handleCopyUrl}>Копировать URL</Button>
        </Space>
      )}
    >
      {loading && <div style={{ textAlign: 'center', padding: 50 }}><Spin /></div>}
      {!loading && error && (
        <Alert type="error" message={error} style={{ marginBottom: 12 }} />
      )}
      {!loading && file && (
        <Editor
          height="calc(100vh - 220px)"
          language={LANGUAGE_BY_FORMAT[file.format]}
          value={content}
          onChange={(v) => setContent(v == null ? '' : v)}
          onMount={(editor) => { editorRef.current = editor; }}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            wordWrap: 'on',
            scrollBeyondLastLine: false,
          }}
        />
      )}
    </Drawer>
  );
}

export default ConfigEditor;
